// CASL <https://github.com/stalniy/casl> based authorization setup for K4Ops.

import { get, isFunction, map } from "lodash";
import { Ability, AbilityBuilder, ForbiddenError } from "@casl/ability";

// Expose underlying ForbiddenError exception type. Downstream users of this
// service don't need to know where it comes from, just what to check for.
export { ForbiddenError } from "@casl/ability";

const rolesMap = Object.freeze({
	admin(user, { can, cannot }) {
		// Users with admin role get blanket permission to do everything.
		can("manage", "all");
		// lock access is not part of admin - must be expressly granted
		cannot("manage", "LockManager");
	},

	lockUser(user, { can }) {
		can("read", "LockManager");
	},

	lockAdmin(user, { can }) {
		can("manage", "LockManager");
	},

	support(user, { can }) {
		can("read", "LiveView");
		can("read", "K4NOC");
	},

	advancedSupport(user, { can }) {
		can("manage", "LiveView");
		can("manage", "K4NOC");
		can("manage", "Scenes");
		can("manage", "Users");
	},

	checkIn(user, { can }) {
		can("Check-in", "Users");
	},

	projectManager(user, { can }) {
		can("read", "Report"); // Can view (all) DOMO reports
	},

	advancedProjectManage(user, { can }) {
		can("manage", "Report"); // Can view (all) DOMO reports
		can("manage", "AccessGroups");
	},

	ops(user, { can }) {
		can("access", "Ops");
		can("list", "Device");
		can("read", "Device");
		can("command", "Device"); // Can execute commands on a device
		can("test", "Device"); // Can run device tests
	},

	installer(user, { can }) {
		can("install", "System"); // Can navigate installation flow
		can("create", "Device");
		can("delete", "Device");
		can("update", "Device"); // Can move a device and change properties
	},


});
export const supportedRoles = Object.freeze(Object.keys(rolesMap));

const permissionsMap = Object.freeze({
	
});

export const supportedPermissions = Object.freeze(Object.keys(permissionsMap));

const assocTypesMap = {
	community(user, { can, cannot }, communities) {

		const roles = get(user, "_roles", []);
		const communityIds = map(communities, "_id");

		const grantAccess = roles.some(
			role => ["advocate", "support", "advancedSupport", "admin"].includes(role._name)
		);
		if (grantAccess) {
			// If user has any of those roles, allow access to any community
			can("read", "Community");
		}
		else {
			// Else grant access based on association
			cannot("manage", "Community", { id: { $nin: communityIds } }).because(
				"User has no association to this community"
			);
			can("read", "Community", { id: { $in: communityIds } });
		}
	},
};
export const supportedAssocTypes = Object.freeze(Object.keys(assocTypesMap));


// The shared "ability" of the current user. This should be used throughout the
// rest of the app for `can` and `cannot` checks. Starts as an anonymous user
// and needs to be replaced/updated via `defineAbilityFor` once a user is
// authenticated.
const ability = defineAbilityFor(null);
export default ability;


export function defineAbilityFor(user) {
	const builder = new AbilityBuilder(Ability);

	// Assign permissions based on k4 roles
	const roles = get(user, "_roles", []);
	for (const role of roles)
	{
		if (isFunction(rolesMap[role._name])) rolesMap[role._name](user, builder);
	}

	// Assign permissions based on k4 permissions attached to roles
	const permissions = get(user, "scope", []);

	for (const permission of permissions)
	{
		if (isFunction(permissionsMap[permission]))
			permissionsMap[permission](user, builder);
	}

	const communities = get(user, "_communities", []);
	assocTypesMap.community(user, builder, communities)
		
	return builder.build();
}

// Utility function to throw an error if ability check fails.
export function throwUnlessCan(ability, ...args) {
	return ForbiddenError.from(ability).throwUnlessCan(...args);
}
